/**
 * BarberProfilePage - Professional profile for barbers
 * Shows performance stats, schedule, and professional information
 */

import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  collection,
  query,
  where,
  getDocs,
  onSnapshot,
  orderBy,
  limit
} from 'firebase/firestore';
import { ref, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import { db, storage } from '@/lib/firebase';
import { useAuth } from '@/contexts/AuthContext';
import { branchService } from '@/services/branchService';
import { getBarberByUserId } from '@/services/barberService';
import { toast } from '@/lib/toast';
import ImageCropModal from '@/components/modals/ImageCropModal';
import {
  TrendingUp,
  Users,
  CheckCircle,
  Clock,
  Star,
  Calendar,
  MapPin,
  Camera,
  DollarSign,
  Wallet
} from 'lucide-react';
import type { Branch } from '@/types';

interface BarberEarnings {
  totalEarnings: number;
  totalCommission: number;
  totalServices: number;
  periodLabel: string;
  servicesBreakdown: Array<{
    serviceName: string;
    count: number;
    totalPrice: number;
    totalCommission: number;
  }>;
}

interface BarberStats {
  totalCompleted: number;
  completedToday: number;
  completedThisWeek: number;
  completedThisMonth: number;
  averageServiceTime: number;
  totalClientsServed: number;
  noShowRate: number;
}

interface RecentService {
  queueId: string;
  completedAt: Date;
  serviceDuration: number;
  ticketNumber: string;
}

export default function BarberProfilePage() {
  const navigate = useNavigate();
  const { user, firebaseUser, signOut, customClaims, loading: authLoading } = useAuth();
  const [stats, setStats] = useState<BarberStats>({
    totalCompleted: 0,
    completedToday: 0,
    completedThisWeek: 0,
    completedThisMonth: 0,
    averageServiceTime: 0,
    totalClientsServed: 0,
    noShowRate: 0,
  });
  const [recentServices, setRecentServices] = useState<RecentService[]>([]);
  const [branch, setBranch] = useState<Branch | null>(null);
  const [loading, setLoading] = useState(true);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [cropImageSrc, setCropImageSrc] = useState<string | null>(null);
  const [earnings, setEarnings] = useState<BarberEarnings | null>(null);
  const [earningsLoading, setEarningsLoading] = useState(false);
  const [earningsPeriod, setEarningsPeriod] = useState<'day' | 'week' | 'month' | 'year'>('month');

  // Get the correct barberId from the barbers collection using userId
  const [barberId, setBarberId] = useState<string | undefined>(undefined);
  const [barberNotFound, setBarberNotFound] = useState(false);
  const branchId = user?.branchId || customClaims?.branchId;

  useEffect(() => {
    async function fetchBarberId() {
      // Wait for auth to finish loading
      if (authLoading) return;

      if (!firebaseUser?.uid) {
        setBarberId(undefined);
        setLoading(false);
        return;
      }

      try {
        const barber = await getBarberByUserId(firebaseUser.uid);
        if (barber) {
          setBarberId(barber.barberId);
          setBarberNotFound(false);
        } else {
          console.warn('No barber document found for userId:', firebaseUser.uid);
          setBarberId(undefined);
          setBarberNotFound(true);
          setLoading(false);
        }
      } catch (error) {
        console.error('Error fetching barber:', error);
        setBarberId(undefined);
        setBarberNotFound(true);
        setLoading(false);
      }
    }

    fetchBarberId();
  }, [firebaseUser?.uid, authLoading]);

  // Get initials from name
  const getInitials = (name: string) => {
    const words = name.trim().split(' ');
    if (words.length >= 2) {
      return (words[0][0] + words[1][0]).toUpperCase();
    } else {
      return name.substring(0, 2).toUpperCase();
    }
  };

  useEffect(() => {
    if (!barberId) return;

    const loadData = async () => {
      setLoading(true);
      try {
        await Promise.all([
          loadStats(),
          loadRecentServices(),
          loadBranch(),
        ]);
      } catch (error) {
        console.error('Error loading barber profile:', error);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [barberId, branchId]);

  const loadBranch = async () => {
    if (!branchId) return;

    try {
      const branchData = await branchService.getById(branchId);
      setBranch(branchData);
    } catch (error) {
      console.error('Error loading branch:', error);
    }
  };

  const loadStats = async () => {
    if (!barberId) return;

    try {
      // Get date ranges
      const now = new Date();
      const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      const weekStart = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
      const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);

      // Query completed services
      const completedQuery = query(
        collection(db, 'queues'),
        where('barberId', '==', barberId),
        where('status', '==', 'completed')
      );
      const completedSnapshot = await getDocs(completedQuery);

      const completedDocs = completedSnapshot.docs.map(doc => ({
        ...doc.data(),
        completedAt: doc.data().completedAt?.toDate(),
      }));

      // Calculate stats
      const totalCompleted = completedDocs.length;
      const completedToday = completedDocs.filter(
        d => d.completedAt && d.completedAt >= todayStart
      ).length;
      const completedThisWeek = completedDocs.filter(
        d => d.completedAt && d.completedAt >= weekStart
      ).length;
      const completedThisMonth = completedDocs.filter(
        d => d.completedAt && d.completedAt >= monthStart
      ).length;

      // Calculate average service time
      const servicesWithDuration = completedDocs.filter((d: any) => d.estimatedDuration);
      const averageServiceTime = servicesWithDuration.length > 0
        ? servicesWithDuration.reduce((sum: number, d: any) => sum + (d.estimatedDuration || 0), 0) / servicesWithDuration.length
        : 0;

      // Get unique clients
      const uniqueClients = new Set(completedDocs.map((d: any) => d.userId));
      const totalClientsServed = uniqueClients.size;

      // Calculate no-show rate
      let totalNoShows = 0;
      try {
        const noShowQuery = query(
          collection(db, 'queues'),
          where('barberId', '==', barberId),
          where('status', '==', 'expired')
        );
        const noShowSnapshot = await getDocs(noShowQuery);
        totalNoShows = noShowSnapshot.size;
      } catch (noShowError) {
        console.warn('Error loading no-show stats:', noShowError);
      }

      const noShowRate = totalCompleted + totalNoShows > 0
        ? (totalNoShows / (totalCompleted + totalNoShows)) * 100
        : 0;

      setStats({
        totalCompleted,
        completedToday,
        completedThisWeek,
        completedThisMonth,
        averageServiceTime: Math.round(averageServiceTime),
        totalClientsServed,
        noShowRate: Math.round(noShowRate * 10) / 10,
      });
    } catch (error: any) {
      console.error('Error loading stats:', error);
      // If it's an index error, show a more helpful message
      if (error?.code === 'failed-precondition' || error?.message?.includes('index')) {
        toast.error('Error de configuración. Contacta al administrador.');
      }
    }
  };

  const loadRecentServices = async () => {
    if (!barberId) return;

    try {
      const recentQuery = query(
        collection(db, 'queues'),
        where('barberId', '==', barberId),
        where('status', '==', 'completed'),
        orderBy('completedAt', 'desc'),
        limit(10)
      );
      const recentSnapshot = await getDocs(recentQuery);

      const services = recentSnapshot.docs.map(doc => ({
        queueId: doc.id,
        ...doc.data(),
        completedAt: doc.data().completedAt?.toDate(),
      })) as RecentService[];

      setRecentServices(services);
    } catch (error: any) {
      console.error('Error loading recent services:', error);
      // If it's an index error, the page still works - just no recent services
      if (error?.code === 'failed-precondition' || error?.message?.includes('index')) {
        console.warn('Index required for recent services query');
      }
    }
  };

  // Real-time earnings listener
  useEffect(() => {
    if (!barberId || !firebaseUser?.uid) return;

    setEarningsLoading(true);

    // Calculate period dates
    const now = new Date();
    let periodStart: Date;

    switch (earningsPeriod) {
      case 'day':
        periodStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        break;
      case 'week':
        periodStart = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
        break;
      case 'month':
        periodStart = new Date(now.getFullYear(), now.getMonth(), 1);
        break;
      case 'year':
        periodStart = new Date(now.getFullYear(), 0, 1);
        break;
      default:
        periodStart = new Date(now.getFullYear(), now.getMonth(), 1);
    }

    // Real-time listener for barber earnings
    const paymentsQuery = query(
      collection(db, 'payments'),
      where('barberUserId', '==', firebaseUser.uid),
      where('completedAt', '>=', periodStart),
      where('completedAt', '<=', now),
      orderBy('completedAt', 'desc')
    );

    const unsubscribe = onSnapshot(paymentsQuery, (snapshot) => {
      let totalEarnings = 0;
      let totalCommission = 0;
      const serviceMap = new Map<string, { count: number; totalPrice: number; totalCommission: number }>();

      snapshot.forEach((doc) => {
        const payment = doc.data();
        const earnings = payment.barberEarnings || payment.commissionAmount || 0;
        const servicePrice = payment.totalAmount || payment.servicePrice || 0;

        totalEarnings += servicePrice;
        totalCommission += earnings;

        const serviceName = payment.serviceName || 'Servicio';
        const existing = serviceMap.get(serviceName) || { count: 0, totalPrice: 0, totalCommission: 0 };
        existing.count += 1;
        existing.totalPrice += servicePrice;
        existing.totalCommission += earnings;
        serviceMap.set(serviceName, existing);
      });

      const servicesBreakdown = Array.from(serviceMap.entries()).map(([name, stats]) => ({
        serviceName: name,
        count: stats.count,
        totalPrice: stats.totalPrice,
        totalCommission: stats.totalCommission,
      }));

      setEarnings({
        totalEarnings,
        totalCommission,
        totalServices: snapshot.size,
        periodLabel: earningsPeriod === 'day' ? 'Hoy' :
                     earningsPeriod === 'week' ? 'Esta semana' :
                     earningsPeriod === 'month' ? 'Este mes' : 'Este año',
        servicesBreakdown,
      });
      setEarningsLoading(false);
    }, (error) => {
      console.error('Error loading earnings:', error);
      toast.error('Error al cargar las ganancias');
      setEarningsLoading(false);
    });

    return () => unsubscribe();
  }, [barberId, earningsPeriod, firebaseUser?.uid]);

  const handlePhotoUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file || !firebaseUser) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Por favor selecciona una imagen válida');
      return;
    }

    if (file.size > 2 * 1024 * 1024) {
      toast.error('La imagen debe ser menor a 2MB');
      return;
    }

    const reader = new FileReader();
    reader.onload = () => setCropImageSrc(reader.result as string);
    reader.readAsDataURL(file);
    // Reset so the same file can be re-selected
    event.target.value = '';
  };

  const uploadCroppedPhoto = async (blob: Blob) => {
    if (!firebaseUser) return;
    setCropImageSrc(null);
    setUploadingPhoto(true);
    const toastId = toast.loading('Subiendo foto de perfil...');

    try {
      const storageRef = ref(storage, `users/${firebaseUser.uid}/profile_${Date.now()}.jpg`);
      const uploadTask = uploadBytesResumable(storageRef, blob);

      uploadTask.on('state_changed',
        (snapshot) => {
          const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
          toast.loading(`Subiendo... ${progress.toFixed(0)}%`, { id: toastId });
        },
        (error) => {
          console.error('Error uploading photo:', error);
          toast.error('Error al subir la foto', { id: toastId });
          setUploadingPhoto(false);
        },
        async () => {
          const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);

          const { doc, updateDoc } = await import('firebase/firestore');
          await updateDoc(doc(db, 'users', firebaseUser.uid), {
            photoURL: downloadURL,
            updatedAt: new Date(),
          });

          if (barberId) {
            try {
              await updateDoc(doc(db, 'barbers', barberId), {
                photoURL: downloadURL,
                updatedAt: new Date(),
              });
            } catch (err) {
              console.warn('Could not update barber photo:', err);
            }
          }

          toast.success('¡Foto de perfil actualizada!', { id: toastId });
          setUploadingPhoto(false);
          window.location.reload();
        }
      );
    } catch (error) {
      console.error('Error in photo upload:', error);
      toast.error('Error al subir la foto', { id: toastId });
      setUploadingPhoto(false);
    }
  };

  const handleLogout = async () => {
    try {
      await signOut();
      toast.success('Sesión cerrada exitosamente');
      navigate('/login');
    } catch (error) {
      console.error('Error logging out:', error);
      toast.error('Error al cerrar sesión');
    }
  };

  if (loading && !barberNotFound) {
    return (
      <div className="min-h-screen bg-surface-2 flex items-center justify-center">
        <div className="text-center">
          <div className="text-4xl mb-4">⏳</div>
          <p className="text-ink-muted">Cargando perfil...</p>
        </div>
      </div>
    );
  }

  if (barberNotFound) {
    return (
      <div className="min-h-screen bg-surface-2 flex items-center justify-center">
        <div className="text-center max-w-md mx-auto p-6">
          <div className="text-6xl mb-4">💈</div>
          <h2 className="text-2xl font-bold text-ink mb-2">
            Perfil de barbero no encontrado
          </h2>
          <p className="text-ink-muted mb-6">
            No se encontró un perfil de barbero asociado a tu cuenta. Contacta con el administrador para que te asigne como barbero.
          </p>
          <button
            onClick={() => navigate('/dashboard')}
            className="px-6 py-3 bg-accent/100 hover:bg-accent/90 text-on-accent rounded-lg font-semibold transition-colors"
          >
            Volver al Dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-surface-2 py-8 px-4">
      <div className="max-w-6xl mx-auto space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-3xl font-bold text-ink mb-2">
            Mi Perfil Profesional
          </h1>
          <p className="text-ink-muted">
            Estadísticas y configuración de tu cuenta
          </p>
        </div>

        {/* Profile Card */}
        <div className="bg-surface rounded-xl shadow-lg p-6 border border-hairline">
          <div className="flex flex-col md:flex-row gap-6 items-start md:items-center">
            {/* Photo */}
            <div className="relative">
              {user?.photoURL ? (
                <img
                  src={user.photoURL}
                  alt={user.displayName}
                  className="w-32 h-32 rounded-full object-cover ring-4 ring-accent/30"
                />
              ) : (
                <div className="w-32 h-32 rounded-full flex items-center justify-center text-5xl font-black text-white ring-4 ring-accent/30">
                  {user?.displayName ? getInitials(user.displayName) : '💈'}
                </div>
              )}
              <button
                onClick={() => fileInputRef.current?.click()}
                disabled={uploadingPhoto}
                className="absolute bottom-0 right-0 bg-accent/100 hover:bg-accent/90 text-ink p-3 rounded-full shadow-lg transition-colors disabled:bg-surface-3"
                title="Cambiar foto"
              >
                <Camera size={20} />
              </button>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handlePhotoUpload}
                className="hidden"
              />
            </div>

            {/* Info */}
            <div className="flex-1">
              <h2 className="text-2xl font-bold text-ink mb-1">
                {user?.displayName || 'Barbero'}
              </h2>
              <p className="text-ink-muted mb-3">{user?.email}</p>

              {branch && (
                <div className="flex items-center gap-2 text-ink-muted mb-3">
                  <MapPin size={16} className="text-accent" />
                  <span className="font-medium">{branch.name}</span>
                  <span className="text-ink-subtle">•</span>
                  <span className="text-sm">{branch.city}</span>
                </div>
              )}

              <div className="flex items-center gap-2 bg-accent/10 px-4 py-2 rounded-lg inline-flex">
                <Star className="text-accent" size={20} />
                <span className="font-semibold text-ink">
                  Barbero Profesional
                </span>
              </div>
            </div>

            {/* Logout Button */}
            <button
              onClick={handleLogout}
              className="px-6 py-3 bg-danger hover:bg-danger/90 text-white rounded-lg font-semibold transition-colors"
            >
              Cerrar Sesión
            </button>
          </div>
        </div>

        {/* Performance Stats */}
        <div className="bg-surface rounded-xl shadow-lg p-6 border border-hairline">
          <div className="flex items-center gap-2 mb-6">
            <TrendingUp className="text-accent" size={24} />
            <h2 className="text-2xl font-bold text-ink">
              Estadísticas de Rendimiento
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Total Completed */}
            <div className="rounded-xl p-6 text-white">
              <div className="flex items-center justify-between mb-2">
                <CheckCircle className="w-8 h-8 opacity-80" />
                <span className="text-4xl font-black">{stats.totalCompleted}</span>
              </div>
              <h3 className="text-sm font-medium opacity-90">Total Completados</h3>
            </div>

            {/* Completed Today */}
            <div className="rounded-xl p-6 text-white">
              <div className="flex items-center justify-between mb-2">
                <Calendar className="w-8 h-8 opacity-80" />
                <span className="text-4xl font-black">{stats.completedToday}</span>
              </div>
              <h3 className="text-sm font-medium opacity-90">Hoy</h3>
            </div>

            {/* This Week */}
            <div className="rounded-xl p-6 text-white">
              <div className="flex items-center justify-between mb-2">
                <Calendar className="w-8 h-8 opacity-80" />
                <span className="text-4xl font-black">{stats.completedThisWeek}</span>
              </div>
              <h3 className="text-sm font-medium opacity-90">Esta Semana</h3>
            </div>

            {/* This Month */}
            <div className="rounded-xl p-6 text-white">
              <div className="flex items-center justify-between mb-2">
                <Calendar className="w-8 h-8 opacity-80" />
                <span className="text-4xl font-black">{stats.completedThisMonth}</span>
              </div>
              <h3 className="text-sm font-medium opacity-90">Este Mes</h3>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
            {/* Average Service Time */}
            <div className="bg-surface-2 rounded-xl p-6 border border-hairline">
              <div className="flex items-center gap-3 mb-2">
                <Clock className="text-accent" size={20} />
                <h3 className="font-semibold text-ink">Tiempo Promedio</h3>
              </div>
              <p className="text-3xl font-black text-ink">
                {stats.averageServiceTime} min
              </p>
            </div>

            {/* Total Clients Served */}
            <div className="bg-surface-2 rounded-xl p-6 border border-hairline">
              <div className="flex items-center gap-3 mb-2">
                <Users className="text-accent" size={20} />
                <h3 className="font-semibold text-ink">Clientes Atendidos</h3>
              </div>
              <p className="text-3xl font-black text-ink">
                {stats.totalClientsServed}
              </p>
            </div>

            {/* No-Show Rate */}
            <div className="bg-surface-2 rounded-xl p-6 border border-hairline">
              <div className="flex items-center gap-3 mb-2">
                <TrendingUp className="text-accent" size={20} />
                <h3 className="font-semibold text-ink">Tasa de No-Show</h3>
              </div>
              <p className="text-3xl font-black text-ink">
                {stats.noShowRate}%
              </p>
            </div>
          </div>
        </div>

        {/* Earnings Panel */}
        <div className="bg-surface rounded-xl shadow-lg p-6 border border-hairline">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-2">
              <Wallet className="text-success" size={24} />
              <h2 className="text-2xl font-bold text-ink">
                Mis Ganancias
              </h2>
            </div>

            {/* Period Selector */}
            <div className="flex gap-2">
              {(['day', 'week', 'month', 'year'] as const).map((period) => (
                <button
                  key={period}
                  onClick={() => setEarningsPeriod(period)}
                  disabled={earningsLoading}
                  className={`px-3 py-1.5 text-sm rounded-lg font-medium transition ${
                    earningsPeriod === period
                      ? 'bg-success text-white'
                      : 'bg-surface-2 text-ink-muted hover:bg-surface-3'
                  } disabled:opacity-50`}
                >
                  {period === 'day' ? 'Hoy' : period === 'week' ? 'Semana' : period === 'month' ? 'Mes' : 'Año'}
                </button>
              ))}
            </div>
          </div>

          {earningsLoading ? (
            <div className="text-center py-8">
              <div className="animate-spin text-4xl">💰</div>
              <p className="text-ink-muted mt-2">Cargando ganancias...</p>
            </div>
          ) : earnings ? (
            <>
              {/* Main Earnings Cards */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                {/* Total Commission */}
                <div className="rounded-xl p-6 text-white">
                  <div className="flex items-center justify-between mb-2">
                    <DollarSign className="w-8 h-8 opacity-80" />
                    <span className="text-3xl font-black">
                      {(earnings.totalCommission / 100).toFixed(2)}€
                    </span>
                  </div>
                  <h3 className="text-sm font-medium opacity-90">Mi Comisión</h3>
                  <p className="text-xs opacity-75 mt-1">{earnings.periodLabel}</p>
                </div>

                {/* Total Sales */}
                <div className="rounded-xl p-6 text-white">
                  <div className="flex items-center justify-between mb-2">
                    <TrendingUp className="w-8 h-8 opacity-80" />
                    <span className="text-3xl font-black">
                      {(earnings.totalEarnings / 100).toFixed(2)}€
                    </span>
                  </div>
                  <h3 className="text-sm font-medium opacity-90">Total Ventas</h3>
                  <p className="text-xs opacity-75 mt-1">{earnings.periodLabel}</p>
                </div>

                {/* Services Count */}
                <div className="rounded-xl p-6 text-white">
                  <div className="flex items-center justify-between mb-2">
                    <CheckCircle className="w-8 h-8 opacity-80" />
                    <span className="text-3xl font-black">{earnings.totalServices}</span>
                  </div>
                  <h3 className="text-sm font-medium opacity-90">Servicios</h3>
                  <p className="text-xs opacity-75 mt-1">{earnings.periodLabel}</p>
                </div>
              </div>

              {/* Services Breakdown */}
              {earnings.servicesBreakdown && earnings.servicesBreakdown.length > 0 && (
                <div className="bg-surface-2 rounded-xl p-4">
                  <h3 className="font-semibold text-ink mb-4">
                    Desglose por Servicio
                  </h3>
                  <div className="space-y-3">
                    {earnings.servicesBreakdown.map((service, index) => (
                      <div
                        key={index}
                        className="flex items-center justify-between p-3 bg-surface rounded-lg border border-hairline"
                      >
                        <div>
                          <p className="font-medium text-ink">
                            {service.serviceName}
                          </p>
                          <p className="text-sm text-ink-muted">
                            {service.count} {service.count === 1 ? 'servicio' : 'servicios'}
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="font-bold text-success">
                            +{(service.totalCommission / 100).toFixed(2)}€
                          </p>
                          <p className="text-xs text-ink-subtle">
                            de {(service.totalPrice / 100).toFixed(2)}€
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="text-center py-8">
              <div className="text-4xl mb-2">💸</div>
              <p className="text-ink-muted">
                No hay ganancias registradas en este período
              </p>
            </div>
          )}
        </div>

        {/* Recent Services */}
        <div className="bg-surface rounded-xl shadow-lg p-6 border border-hairline">
          <h2 className="text-2xl font-bold text-ink mb-4">
            Servicios Recientes
          </h2>

          {recentServices.length === 0 ? (
            <p className="text-ink-muted text-center py-8">
              No hay servicios completados aún
            </p>
          ) : (
            <div className="space-y-3">
              {recentServices.map((service) => (
                <div
                  key={service.queueId}
                  className="flex items-center justify-between p-4 bg-surface-2 rounded-lg border border-hairline"
                >
                  <div>
                    <p className="font-semibold text-ink">
                      Turno #{service.ticketNumber}
                    </p>
                    <p className="text-sm text-ink-muted">
                      {service.completedAt?.toLocaleDateString('es-AR', {
                        day: '2-digit',
                        month: 'short',
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit',
                      })}
                    </p>
                  </div>
                  {service.serviceDuration && (
                    <div className="text-right">
                      <p className="text-sm text-ink-muted">Duración</p>
                      <p className="font-semibold text-ink">
                        {service.serviceDuration} min
                      </p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Quick Actions */}
        <div className="bg-surface rounded-xl shadow-lg p-6 border border-hairline">
          <h2 className="text-2xl font-bold text-ink mb-4">
            Acciones Rápidas
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <button
              onClick={() => navigate('/barber-queue')}
              className="p-6 rounded-xl text-white shadow-lg hover:shadow-xl transition-all"
            >
              <h3 className="text-lg font-bold mb-1">Gestionar Cola</h3>
              <p className="text-sm opacity-90">Ver mis turnos pendientes</p>
            </button>

            <button
              onClick={() => navigate('/appointments')}
              className="p-6 rounded-xl text-white shadow-lg hover:shadow-xl transition-all"
            >
              <h3 className="text-lg font-bold mb-1">Mis Citas</h3>
              <p className="text-sm opacity-90">Ver calendario</p>
            </button>

            <button
              onClick={() => navigate('/dashboard')}
              className="p-6 rounded-xl text-white shadow-lg hover:shadow-xl transition-all"
            >
              <h3 className="text-lg font-bold mb-1">Dashboard</h3>
              <p className="text-sm opacity-90">Volver al inicio</p>
            </button>
          </div>
        </div>
      </div>

      <ImageCropModal
        isOpen={!!cropImageSrc}
        imageSrc={cropImageSrc || ''}
        onConfirm={uploadCroppedPhoto}
        onClose={() => setCropImageSrc(null)}
      />
    </div>
  );
}
